import _ from 'lodash';

import { selectCommercialBillingApiRoute } from '@/components/SiderMenu/utils/sessionNavigation';
import { initialPagination, initialState, makeListReducers } from '@/models/utils';
import { bindServices } from '@/services/api';
import CoreQueries from '@/utils/input/queries';

import { DEFAULT_ORDER_BY, MODEL_NAME, NAVIGATION_KEY } from './constants';
import { filtersConfig } from './utils';

const api = bindServices(MODEL_NAME);
const pagedQuery = CoreQueries.ChannelPlan.paged;

/** Pattern X — commercial billing CRUD; API base from session navigation. */
const moduleInitialState = {
  ...initialState,
  list: [],
  orderBy: DEFAULT_ORDER_BY,
  select: pagedQuery.select,
  searchSelect: pagedQuery.searchSelect,
  showForm: false,
  detail: false,
  id: null,
  item: null,
  success: false,
  activatingItemIds: {},
  filtersConfig,
};

const ChannelPlansModel = {
  namespace: MODEL_NAME,
  state: moduleInitialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        const pagination = yield select((state) => state[MODEL_NAME].pagination);
        const pageSize = yield select((state) => state.global.pageSize);
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params: {
            limit: pageSize,
            ...pagedQuery,
            ...pagination,
            ...payload,
          },
          readPriority: true,
        });

        const items = res?.items || res?.data || [];
        yield put({
          type: 'queryData',
          payload: {
            ...res,
            items,
            limit: pageSize,
            page: payload.page,
            totalCount: res?.totalCount ?? res?.count ?? res?.total ?? items.length,
          },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchItem({ payload }, { call, put, select }) {
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        yield put({ type: 'success', payload: { success: false } });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: {
            ...(payload.detail ? CoreQueries.ChannelPlan?.itemDetail : {}),
            ...(payload?.params && { ...payload.params }),
          },
        });
        const item = Array.isArray(res) ? res[0] : res;
        yield put({ type: 'queryItem', payload: item });
      } catch (e) {
        /*  */
      }
    },

    *fetchCreate({ payload }, { put, call, select }) {
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        yield put({ type: 'success', payload: { success: false } });
        yield call(api.create, { route: fetchRoute, data: payload.data });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0]?.field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: { validationErrors: e.validationErrors || [], message: e.message },
          });
        }
      }
    },

    *fetchUpdate({ payload }, { put, call, select }) {
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        const id = payload.data?.id;
        if (!id) return;

        yield put({ type: 'success', payload: { success: false } });
        yield call(api.updateById, { route: fetchRoute, id, data: payload.data });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0]?.field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: { validationErrors: e.validationErrors || [], message: e.message },
          });
        }
      }
    },

    *fetchActivate({ payload }, { put, call, select }) {
      const { id } = payload;
      yield put({ type: 'queryActivatingItem', payload: { id, active: true } });
      try {
        const fetchRoute = yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select);
        if (!fetchRoute) return;

        yield call(api.activate, { route: fetchRoute, id, isActive: payload.state });
        yield put({ type: 'channelPlanActivate', payload });
      } catch (e) {
        /*  */
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { id, active: false } });
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({
        type: 'queryOrderBy',
        payload,
      });
    },

    *showForm({ payload }, { put }) {
      yield put({
        type: 'queryShowForm',
        payload,
      });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },
  },

  reducers: {
    ...makeListReducers({
      initialPagination,
      initialState: moduleInitialState,
    }),
    queryActivatingItem(state, { payload }) {
      const { id, ids, active } = payload || {};
      const activatingItemIds = { ...(state.activatingItemIds || {}) };

      if (Array.isArray(ids) && ids.length) {
        ids.forEach((itemId) => {
          if (itemId == null) return;
          if (active) {
            activatingItemIds[itemId] = true;
          } else {
            delete activatingItemIds[itemId];
          }
        });
        return { ...state, activatingItemIds };
      }

      if (id == null) {
        return { ...state, activatingItemIds: {} };
      }

      if (active) {
        activatingItemIds[id] = true;
      } else {
        delete activatingItemIds[id];
      }
      return { ...state, activatingItemIds };
    },
    channelPlanActivate(state, { payload }) {
      const list = state.list.map((item) =>
        item.id === payload.id ? { ...item, isActive: payload.state } : item,
      );
      return { ...state, list };
    },
    setFiltersConfig(state, { payload }) {
      return {
        ...state,
        filtersConfig: payload,
      };
    },
  },
};

export default ChannelPlansModel;
